export const AZIENDE_PAGES_EN = [
  {
    path: '/soluzioni/aziende',
    kind: 'area',
    area: 'Businesses',
    areaPath: '/soluzioni/aziende',
    eyebrow: 'Businesses area',
    title: 'Business insurance in Switzerland: people, liability and continuity.',
    summary: 'Risk analysis and coordinated insurance programmes for Swiss companies and SMEs: employees, liability, property, cyber, business interruption and pensions.',
    seoTitle: 'Business insurance for Swiss companies and SMEs',
    seoDescription: 'Insurance broker for companies and SMEs in Switzerland: accident insurance, pensions, liability, cyber, property and business interruption. Request a review.',
    keywords: ['business insurance switzerland', 'SME insurance switzerland', 'insurance broker business', 'commercial liability switzerland'],
    image: '/assets/soluzione-aziende.png',
    updated: 'June 2026',
    readingTime: '5 min',
    intro: [
      'A company does not need a list of policies: it needs to know which events could compromise its continuity and how to protect itself. Our work starts from a risk analysis, not from a catalogue.',
      'The review considers employee obligations, third-party liability, property and infrastructure, data, key-person dependencies and the company’s ability to absorb a major loss or interruption.',
    ],
    sections: [
      {
        id: 'aree-rischio', type: 'cards', title: 'Analysis by risk area',
        cards: [
          { title: 'People', text: 'Accidents, illness, employee pensions and protection for the owner.' },
          { title: 'Liability', text: 'Damage to third parties, professional errors, liability of corporate bodies.' },
          { title: 'Property', text: 'Premises, machinery, inventory, electronics, goods in transit.' },
          { title: 'Continuity', text: 'Business interruption, loss of income, dependence on key suppliers.' },
          { title: 'Data and cyber', text: 'Cyber attacks, data loss, privacy liability, fraud.' },
          { title: 'Mobility', text: 'Company vehicles, fleets, business travel and trips.' },
        ],
      },
      {
        id: 'metodo', type: 'timeline', title: 'The business advisory method',
        steps: [
          { label: '01', title: 'Interview', text: 'We listen to the owner: activity, clients, processes, concerns.' },
          { label: '02', title: 'Collection', text: 'We analyse existing contracts and available documentation.' },
          { label: '03', title: 'Mapping', text: 'We identify risks, gaps and overlaps in current cover.' },
          { label: '04', title: 'Comparison', text: 'We compare market solutions based on the priorities identified.' },
          { label: '05', title: 'Implementation', text: 'We activate the programme and coordinate the insurers.' },
          { label: '06', title: 'Review', text: 'We periodically review the programme, at every company change.' },
        ],
      },
      {
        id: 'percorsi', type: 'paths', title: 'Insights for businesses',
        links: [
          { href: '/guide/aziende/aprire-un-azienda', label: 'Starting a business', text: 'The practical checklist for those launching an activity.' },
          { href: '/guide/aziende/forme-giuridiche-svizzera', label: 'Legal forms', text: 'Sole proprietorship, Sagl/GmbH, SA/AG: operational and insurance implications.' },
          { href: '/soluzioni/aziende/tipologie-assicurazioni', label: 'Types of insurance', text: 'The categories of cover explained in plain language.' },
        ],
      },
      {
        id: 'settori', type: 'text', title: 'The sectors we serve',
        paragraphs: ['Retail, hospitality, construction, professional firms, technology, industry, services and real estate. Every sector has typical risks: mapping makes them visible before they become a problem.'],
      },
    ],
    faq: [
      { q: 'When should business risks be analysed?', a: 'Ideally before opening, and then at every significant change: hiring, new premises, new machinery, expansion.' },
      { q: 'Do you also work with small businesses?', a: 'Yes: most of the companies we support are small and medium-sized enterprises, where continuity often depends on a few people.' },
      { q: 'Can you manage existing contracts?', a: 'Yes: we collect and analyse current policies, identify overlaps and gaps and coordinate the transition to a coherent programme.' },
    ],
    cta: { title: 'Request a business risk analysis', text: 'A first meeting to understand where your company is exposed and what the priorities are.', label: 'Request the analysis' },
    related: ['/guide/aziende/aprire-un-azienda', '/soluzioni/aziende/tipologie-assicurazioni'],
  },
  {
    path: '/soluzioni/aziende/tipologie-assicurazioni',
    kind: 'service',
    area: 'Businesses',
    areaPath: '/soluzioni/aziende',
    eyebrow: 'Businesses',
    title: 'The main types of business insurance.',
    summary: 'The categories of cover explained in plain language: for each solution, the concrete risk it addresses.',
    updated: 'June 2026',
    readingTime: '8 min',
    intro: [
      'The world of business insurance is full of technical names. Here we translate them into what really matters: which risk they cover and who they are for.',
    ],
    sections: [
      {
        id: 'persone', type: 'cards', title: 'People',
        cards: [
          { title: 'Accident insurance (UVG/LAA)', text: 'Mandatory for employees: covers occupational accidents and, depending on hours worked, non-occupational ones too.' },
          { title: 'Daily sickness allowance', text: 'Tops up salaries in case of employee illness beyond the continued salary payment period.' },
          { title: 'Occupational pension (BVG/LPP)', text: 'The second pillar for employees: mandatory above certain salary thresholds.' },
          { title: 'Key person', text: 'Protects the company if a figure decisive for turnover or know-how is lost.' },
        ],
      },
      {
        id: 'responsabilita', type: 'cards', title: 'Liability',
        cards: [
          { title: 'Business liability', text: 'Damage to third parties caused by the activity, the premises or employees at work.' },
          { title: 'Professional liability', text: 'Consulting, design or service errors: essential for firms and professionals.' },
          { title: 'Product liability', text: 'Damage caused by products sold or manufactured.' },
          { title: 'Directors & officers', text: 'Protects directors and managers from the financial consequences of their decisions.' },
        ],
      },
      {
        id: 'beni', type: 'cards', title: 'Property and continuity',
        cards: [
          { title: 'Inventory and machinery', text: 'Fire, water, theft, natural events on premises, equipment and goods.' },
          { title: 'Electronics', text: 'Breakdown and damage to servers, computers and technical systems.' },
          { title: 'Goods in transit', text: 'Goods during transport, own or entrusted to third parties.' },
          { title: 'Business interruption', text: 'Covers loss of margin and fixed costs if activity stops after an insured event.' },
        ],
      },
      {
        id: 'cyber-giuridica', type: 'cards', title: 'Data, cyber and legal protection',
        cards: [
          { title: 'Cyber risk', text: 'Cyber attacks, data loss, system interruption, recovery costs and privacy liability.' },
          { title: 'Cyber fraud', text: 'Email scams, manipulated payments, social engineering.' },
          { title: 'Legal protection', text: 'Contractual, employment, tenancy, debt collection and traffic disputes.' },
          { title: 'Vehicles and fleets', text: 'Liability, casco and coordinated management of company vehicles.' },
        ],
      },
      {
        id: 'priorita', type: 'warning', title: 'How to set priorities',
        text: 'Not everything needs to be insured immediately: first the risks that can compromise business continuity (liability, owner\u2019s income, business interruption), then frequent risks, finally accessory extensions. The exact order depends on your activity: that is why mapping is needed.',
      },
    ],
    faq: [
      { q: 'Which business insurances are mandatory?', a: 'It depends on the activity and employees: accident and occupational pension are mandatory above certain thresholds; other covers are optional but often decisive. The check must be done on the concrete case.' },
      { q: 'Does a small business really need cyber cover?', a: 'SMEs are among the most frequent targets of attacks, precisely because they are less protected. If you handle client data or depend on systems to invoice, it is worth assessing.' },
      { q: 'Can everything be insured with a single company?', a: 'Sometimes it pays off, sometimes not: some insurers are strong on property, others on people. As brokers we compare and combine the most suitable solutions.' },
    ],
    cta: { title: 'Build your company\u2019s risk map', text: 'We help you understand which of these categories really matter for your activity, and in which order to address them.', label: 'Build the risk map' },
    related: ['/guide/aziende/aprire-un-azienda', '/guide/aziende/forme-giuridiche-svizzera'],
  },
  {
    path: '/guide/aziende/aprire-un-azienda',
    kind: 'guide',
    area: 'Businesses',
    areaPath: '/soluzioni/aziende',
    eyebrow: 'Practical guide · Businesses',
    title: 'What to consider when starting a business.',
    summary: 'The practical checklist for those launching an activity: risks to map, mandatory cover and insurance budget.',
    updated: 'June 2026',
    readingTime: '8 min',
    intro: [
      'Starting a business means focusing on clients and turnover. But a single claim in the first months — damage to a client, a theft, the owner\u2019s illness — can call the whole project into question. Here is how to start protected without waste.',
    ],
    sections: [
      {
        id: 'prima-apertura', type: 'checklist', title: 'Before opening: the questions to ask',
        items: [
          'What activity do I actually carry out and for which clients?',
          'What turnover do I estimate for the first year?',
          'Will I have employees? Immediately or later?',
          'Do I have premises, equipment, vehicles?',
          'Do I handle client data or depend on IT systems?',
          'Does the activity depend entirely on me?',
        ],
      },
      {
        id: 'rischi', type: 'cards', title: 'Risks to map',
        cards: [
          { title: 'Damage to third parties', text: 'A client injured on your premises or by your mistake: business liability is often the first cover to activate.' },
          { title: 'Professional errors', text: 'If you offer advice or services, a mistake can be costly: consider professional liability.' },
          { title: 'Property damage', text: 'Fire, theft, water on premises, equipment and goods.' },
          { title: 'Owner\u2019s illness', text: 'If the activity depends on you, loss of income due to illness is a continuity risk.' },
          { title: 'Cyber and data', text: 'If you handle data or depend on systems, an attack can stop you.' },
          { title: 'Disputes', text: 'Non-paying clients, contractual challenges, problems with the landlord.' },
        ],
      },
      {
        id: 'budget', type: 'text', title: 'The initial insurance budget: how to set priorities',
        paragraphs: [
          'At the start resources are limited: the right order is first the risks that can compromise continuity (liability, owner\u2019s income), then frequent risks (property damage), finally accessory extensions.',
          'A well-built insurance programme can grow with the company: better a few right covers than many policies accumulated without logic.',
        ],
      },
      {
        id: 'checklist-finale', type: 'checklist', title: 'The final checklist',
        items: [
          'Legal form chosen and registered',
          'Employees declared and covered (accidents, BVG/LPP)',
          'Premises and property insured',
          'Liability analysed and activated',
          'Owner\u2019s income protected',
          'Pension provision assessed',
          'Continuity plan prepared',
        ],
      },
    ],
    faq: [
      { q: 'How much budget should I plan for insurance?', a: 'It depends on the activity, employees and property. In the business plan it pays to estimate essential cover as a fixed cost: we help you quantify it before you start.' },
      { q: 'Is everything needed from day one?', a: 'No. Some cover is necessary from the first day (liability, accidents if you have employees), other cover can follow growth. The important thing is to decide consciously, not by chance.' },
      { q: 'What do I risk if I insure nothing?', a: 'The risks remain yours: damage to third parties or a long illness can far exceed the cost of years of premiums, precisely in the most fragile phase of the activity.' },
    ],
    cta: { title: 'Prepare your new business\u2019s insurance plan', text: 'Before opening, let\u2019s define priorities, budget and essential cover together.', label: 'Prepare the plan' },
    related: ['/guide/aziende/forme-giuridiche-svizzera', '/soluzioni/aziende/tipologie-assicurazioni'],
  },
  {
    path: '/guide/aziende/forme-giuridiche-svizzera',
    kind: 'guide',
    area: 'Businesses',
    areaPath: '/soluzioni/aziende',
    eyebrow: 'Practical guide · Businesses',
    title: 'Legal forms in Switzerland: sole proprietorship, Sagl/GmbH, SA/AG.',
    summary: 'The operational, pension and insurance comparison between the main legal forms.',
    updated: 'June 2026',
    readingTime: '7 min',
    intro: [
      'The legal form you choose influences personal liability, pension provision and the insurance you need. This guide illustrates the practical consequences, without replacing legal and tax advice.',
    ],
    sections: [
      {
        id: 'confronto', type: 'cards', title: 'The main forms compared',
        cards: [
          { title: 'Sole proprietorship', text: 'Simple and inexpensive to start. The owner is liable with personal assets and organises accidents, allowances and pension personally.' },
          { title: 'Sagl / GmbH', text: 'Liability limited to the share capital. The working owner is treated as an employee for accidents and pension.' },
          { title: 'SA / AG', text: 'Structure for larger projects or with investors. Higher minimum capital, more formal governance.' },
          { title: 'General partnership', text: 'For two or more individuals: partners are jointly liable. Similar to sole proprietorship on the insurance side.' },
        ],
      },
      {
        id: 'impatto', type: 'text', title: 'The impact on the owner',
        paragraphs: [
          'With a sole proprietorship, the owner is self-employed in every respect: accidents, daily allowances and occupational pension must be organised personally, and liability towards third parties falls on private assets.',
          'With Sagl/GmbH and SA/AG, the owner working in the company is generally treated as an employee: accidents and second pillar go through the company. The liability of corporate bodies and personal income still need protection.',
        ],
      },
      {
        id: 'domande', type: 'checklist', title: 'The questions to ask',
        items: [
          'Will I work alone or with partners?',
          'Do I intend to hire staff?',
          'What capital can I invest at the start?',
          'How much personal risk do I want to take?',
          'Do I need to separate private and business assets?',
          'Do I expect investors or a future sale?',
        ],
      },
      {
        id: 'limite', type: 'warning', title: 'The limit of this guide',
        text: 'The choice of legal form has legal, tax and accounting consequences that require the involvement of a fiduciary, notary or legal advisor. Our role is to clarify its insurance and pension consequences.',
      },
    ],
    faq: [
      { q: 'Is it worth starting as a sole proprietorship?', a: 'For many starting activities yes: it is simple and inexpensive. Conversion to Sagl/GmbH or SA/AG is possible later, as turnover and risks grow.' },
      { q: 'Am I personally protected with a Sagl/GmbH?', a: 'Liability is generally limited to the share capital, but exceptions and officers\u2019 liability remain. Your income and pension must also be organised through the company.' },
      { q: 'What changes for insurance between sole proprietorship and Sagl/GmbH?', a: 'Your status changes: from self-employed (personal cover to activate) to employee-like (accidents and BVG/LPP through the company). Policies must also be registered correctly.' },
    ],
    cta: { title: 'Assess the insurance implications of your chosen form', text: 'Before registering the activity, let\u2019s clarify what changes for you on the insurance and pension side.', label: 'Assess the implications' },
    related: ['/guide/aziende/aprire-un-azienda', '/guide/privati/mettersi-in-proprio'],
  },
];

export const PREVIDENZA_PAGE_EN = {
  path: '/soluzioni/previdenza',
  kind: 'area',
  area: 'Pension',
  areaPath: '/soluzioni/previdenza',
  eyebrow: 'Pension area',
  title: 'Pension planning in Switzerland: AHV/AVS, BVG/LPP, pillar 3a and income protection.',
  summary: 'Review state pension, occupational pension, pillar 3a, disability and family protection to build a sustainable long-term pension plan.',
  seoTitle: 'Pension planning in Switzerland: BVG/LPP and pillar 3a',
  seoDescription: 'Pension analysis in Switzerland covering AHV/AVS, BVG/LPP, pillar 3a and 3b, pension gaps, disability and retirement planning.',
  keywords: ['pension planning switzerland', 'BVG LPP', 'pillar 3a', 'pension analysis switzerland'],
  image: '/assets/soluzione-previdenza.png',
  updated: 'June 2026',
  readingTime: '6 min',
  intro: [
    'In Switzerland pension provision is built on three pillars: state, occupational and private. Understanding how they combine — and where the gaps open — is the first step to protecting your future income.',
    'A coordinated view of AHV/AVS, your occupational pension certificate, pillar 3a, private assets and risk cover helps make retirement, disability and death scenarios visible before choosing additional products.',
  ],
  sections: [
    {
      id: 'tre-livelli', type: 'cards', title: 'The three levels of pension provision',
      cards: [
        { title: 'First pillar: AHV/AVS & IV/AI', text: 'Basic state pension. It covers vital needs, but is rarely enough to maintain your standard of living.' },
        { title: 'Second pillar: BVG/LPP', text: 'Occupational pension through the employer. For the self-employed it must be organised voluntarily.' },
        { title: 'Third pillar', text: 'Private provision: 3a restricted with tax advantages, 3b flexible. This is where gaps are closed.' },
      ],
    },
    {
      id: 'domande', type: 'checklist', title: 'The main questions',
      items: [
        'What income will I have in retirement?',
        'What happens if I can no longer work?',
        'Is my family protected in case of death?',
        'How much can I save each month, sustainably?',
        'Have I changed jobs or started an independent activity?',
        'Am I buying a home?',
      ],
    },
    {
      id: 'protezione-prima', type: 'text', title: 'Protection before saving',
      paragraphs: [
        'Many start saving without having protected their present income. If tomorrow you can no longer work, the savings plan stops: that is why serious planning starts from loss of earnings, death risk and emergency liquidity, and only then looks at returns.',
      ],
    },
    {
      id: 'situazioni', type: 'cards', title: 'Paths by situation',
      cards: [
        { title: 'Employee', text: 'Coordinate BVG/LPP and third pillar, check gaps and the tax advantages of 3a.' },
        { title: 'Self-employed', text: 'Without an employer everything must be rebuilt: accidents, allowances, second and third pillar.' },
        { title: 'Couple and family', text: 'Protecting both incomes, coordinating with mortgage and children.' },
        { title: 'Near retirement', text: 'Buy-ins, early withdrawals, annuity or capital: the decisions of the last years matter greatly.' },
      ],
    },
  ],
  faq: [
    { q: 'What is the difference between pillar 3a and 3b?', a: '3a is restricted, with tax advantages and contribution limits; 3b is flexible, without limits but without the same tax benefits. They are often combined.' },
    { q: 'How much can I pay into the third pillar?', a: '3a limits are set each year by the authorities and vary between employees and the self-employed. We check the current amount during the consultation.' },
    { q: 'Can I use pension funds to buy a home?', a: 'Under certain conditions it is possible to withdraw or pledge second and third pillar funds for owner-occupied property, with effects to assess on pension and taxation.' },
    { q: 'What happens if I become self-employed?', a: 'The second pillar is no longer automatic: without action a gap opens that grows every year. There are BVG/LPP solutions for the self-employed and 3a with higher limits.' },
  ],
  cta: { title: 'Request a pension analysis', text: 'We start from your current situation: gaps, priorities and a realistic plan for the future.', label: 'Request the analysis' },
  related: ['/guide/privati/comprare-casa-a-30-anni', '/guide/privati/mettersi-in-proprio'],
};
